import { useRef } from "react";
import { useForm } from "react-hook-form";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import styled from "styled-components";
import { FiCheck, FiX, FiSearch } from "react-icons/fi";
import {
  Card,
  Input,
  Label,
  FieldError,
  Button,
  GhostButton,
  Spinner,
  SpinnerOverlay,
  Row,
} from "./ui";
import { currencyCode } from "../utils/format";

const libraries = ["places"];

const FormCard = styled(Card)`
  position: relative;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.space(4)};
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.space(4)};
  svg {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    width: 16px; height: 16px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  input { padding-left: 36px; }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RadioCard = styled.label`
  flex: 1 1 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border: 1px solid ${({ theme, checked }) => (checked ? theme.colors.accent : theme.colors.border)};
  background: ${({ theme, checked }) => (checked ? theme.colors.accentSoft : theme.colors.surface)};
  color: ${({ theme, checked }) => (checked ? theme.colors.accent : theme.colors.text)};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  input { display: none; }
  &:hover { border-color: ${({ theme }) => theme.colors.accent}; }
`;

const Footer = styled.div`
  margin-top: ${({ theme }) => theme.space(6)};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space(2)};
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.space(5)};

  &:last-of-type { margin-bottom: 0; }

  .title {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: ${({ theme }) => theme.space(3)};
  }
`;

const AddSiteForm = ({ onConfirm, onCancel }) => {
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      name: "",
      lat: "",
      lon: "",
      address: "",
      instruction: "",
      offering: "CASUAL",
      short_term_price: "",
      long_term_price: "",
    },
    mode: "onChange",
  });

  const autocompleteRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const offering = watch("offering");

  const onPlace = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry) {
      setValue("name", place.name || "", { shouldValidate: true });
      setValue("lat", place.geometry.location.lat().toFixed(6), { shouldValidate: true });
      setValue("lon", place.geometry.location.lng().toFixed(6), { shouldValidate: true });
      setValue("address", place.formatted_address || "", { shouldValidate: true });
    }
  };

  const submit = async (data) => {
    try {
      await onConfirm(data);
    } catch {
      /* parent handles surfacing the error */
    }
  };

  if (loadError) {
    return <FormCard>Error loading Google Maps: {loadError.message}</FormCard>;
  }
  if (!isLoaded) {
    return (
      <FormCard>
        <Row gap={2}><Spinner /> Loading map…</Row>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <form onSubmit={handleSubmit(submit)}>
        <Section>
          <div className="title">Find a place</div>
          <Autocomplete onLoad={(a) => (autocompleteRef.current = a)} onPlaceChanged={onPlace}>
            <SearchWrap>
              <FiSearch />
              <Input type="text" placeholder="Search for a place on Google Maps…" />
            </SearchWrap>
          </Autocomplete>
        </Section>

        <Section>
          <div className="title">Site details</div>
          <Grid>
            <div>
              <Label>Name</Label>
              <Input {...register("name", { required: "Name is required" })} error={!!errors.name} />
              {errors.name && <FieldError>{errors.name.message}</FieldError>}
            </div>
            <div>
              <Label>Latitude</Label>
              <Input {...register("lat", { required: "Latitude is required" })} error={!!errors.lat} disabled />
              {errors.lat && <FieldError>{errors.lat.message}</FieldError>}
            </div>
            <div>
              <Label>Longitude</Label>
              <Input {...register("lon", { required: "Longitude is required" })} error={!!errors.lon} disabled />
              {errors.lon && <FieldError>{errors.lon.message}</FieldError>}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Address</Label>
              <Input {...register("address", { required: "Address is required" })} error={!!errors.address} />
              {errors.address && <FieldError>{errors.address.message}</FieldError>}
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Instruction</Label>
              <Input {...register("instruction", { required: "Instruction is required" })} error={!!errors.instruction} placeholder="How to find this site…" />
              {errors.instruction && <FieldError>{errors.instruction.message}</FieldError>}
            </div>
          </Grid>
        </Section>

        <Section>
          <div className="title">Pricing & offering</div>
          <Label>Offering</Label>
          <RadioGroup>
            {[
              { value: "CASUAL", label: "Casual" },
              { value: "LONG_TERM", label: "Long term" },
              { value: "BOTH", label: "Both" },
            ].map((opt) => (
              <RadioCard key={opt.value} checked={offering === opt.value}>
                <input type="radio" value={opt.value} {...register("offering", { required: "Offering is required" })} />
                {opt.label}
              </RadioCard>
            ))}
          </RadioGroup>
          {errors.offering && <FieldError>{errors.offering.message}</FieldError>}

          <Grid style={{ marginTop: 16 }}>
            <div>
              <Label>Short-term price ({currencyCode}/hr)</Label>
              <Input {...register("short_term_price")} type="number" step="0.01" placeholder="0.00" />
            </div>
            <div>
              <Label>Long-term price ({currencyCode}/wk)</Label>
              <Input {...register("long_term_price")} type="number" step="0.01" placeholder="0.00" />
            </div>
          </Grid>
        </Section>

        <Footer>
          <GhostButton type="button" onClick={onCancel}><FiX /> Cancel</GhostButton>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Spinner size={14} /> : <FiCheck />} Confirm
          </Button>
        </Footer>
      </form>
      {isSubmitting && (
        <SpinnerOverlay>
          <Spinner size={32} />
        </SpinnerOverlay>
      )}
    </FormCard>
  );
};

export default AddSiteForm;
